/**
 * PWA Utility Functions
 *
 * `/manifest.json` is generated at build time. At runtime we patch name/icons
 * into a blob manifest so install prompts use the configurable app name
 * (admin settings / config.json) instead of the build-time default.
 */

import { getConfig } from '@/utils/config';

/** localStorage key for name/logo used at install time */
export const PWA_INSTALL_META_KEY = 'pwa_install_meta';
export const PWA_MANIFEST_PATH = '/manifest.json';
export const PWA_LOGO_PATH = '/upload/tradinglogo.png';
export const PWA_DEFAULT_NAME = 'اپلیکیشن';

let manifestBlobUrl = null;

export function buildPwaLogoUrl(cacheSeed = Date.now()) {
  return `${PWA_LOGO_PATH}?v=${encodeURIComponent(String(cacheSeed))}`;
}

function withFreshLogoCache(url, cacheSeed = Date.now()) {
  if (!url) {
    return buildPwaLogoUrl(cacheSeed);
  }

  try {
    const parsedUrl = new URL(url, window.location.origin);
    parsedUrl.searchParams.set('v', String(cacheSeed));

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return parsedUrl.toString();
    }

    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    const cleanUrl = url.split('?')[0];
    return `${cleanUrl}?v=${encodeURIComponent(String(cacheSeed))}`;
  }
}

/**
 * Absolute URL for the default app icon in public/upload
 */
export function getPublicUploadLogoUrl() {
  const base = import.meta.env.BASE_URL || '/';
  const path = `${base}${withFreshLogoCache(PWA_LOGO_PATH)}`.replace(/\/{2,}/g, '/');
  if (path.startsWith('http')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${window.location.origin}${normalized}`;
}

/** Resolve logo URL; falls back to upload/tradinglogo.png */
function resolveLogoUrl(logoUrl) {
  if (logoUrl) {
    const freshUrl = withFreshLogoCache(logoUrl);
    if (freshUrl.startsWith('http://') || freshUrl.startsWith('https://')) return freshUrl;
    if (freshUrl.startsWith('/')) return `${window.location.origin}${freshUrl}`;
    return `${window.location.origin}/${freshUrl}`;
  }
  return getPublicUploadLogoUrl();
}

/**
 * Check if the app is running as a PWA (installed)
 */
export function isPWAInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
}

export function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
}

/**
 * Persist app name + logo for runtime UI fallback.
 */
export function savePwaInstallMeta(settings) {
  if (!settings) return;
  try {
    localStorage.setItem(PWA_INSTALL_META_KEY, JSON.stringify({
      app_name: settings.app_name || '',
      app_logo: settings.app_logo || '',
      savedAt: Date.now(),
    }));
  } catch {
    /* ignore quota */
  }
}

/**
 * Read saved install meta
 * @returns {{ app_name: string, app_logo: string, savedAt?: number } | null}
 */
export function getPwaInstallMeta() {
  try {
    const raw = localStorage.getItem(PWA_INSTALL_META_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      app_name: typeof parsed.app_name === 'string' ? parsed.app_name : '',
      app_logo: typeof parsed.app_logo === 'string' ? parsed.app_logo : '',
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : undefined,
    };
  } catch {
    return null;
  }
}

/**
 * Merge live settings with localStorage / config.json fallback
 */
export function mergeInstallSettings(settings) {
  const ls = getPwaInstallMeta();
  const configName = getConfig('VITE_APP_NAME', '').trim();
  return {
    app_name: settings?.app_name || ls?.app_name || configName || PWA_DEFAULT_NAME,
    app_logo: withFreshLogoCache(settings?.app_logo || ls?.app_logo || PWA_LOGO_PATH),
  };
}

function shortAppName(name) {
  if (!name) return PWA_DEFAULT_NAME;
  return name.length > 12 ? `${name.slice(0, 11)}\u2026` : name;
}

/**
 * Rewrite <link rel="manifest"> so Chrome/Android install uses the live app name.
 */
async function applyDynamicManifest({ app_name, app_logo }) {
  if (typeof document === 'undefined' || !app_name) return;

  try {
    const res = await fetch(PWA_MANIFEST_PATH, { cache: 'no-cache' });
    if (!res.ok) return;

    const manifest = await res.json();
    const logoUrl = resolveLogoUrl(app_logo);
    const shortName = shortAppName(app_name);

    manifest.name = app_name;
    manifest.short_name = shortName;

    if (logoUrl) {
      manifest.icons = [
        { src: logoUrl, sizes: '192x192', type: 'image/png', purpose: 'any' },
        { src: logoUrl, sizes: '512x512', type: 'image/png', purpose: 'any' },
      ];
    }

    if (manifestBlobUrl) {
      URL.revokeObjectURL(manifestBlobUrl);
    }

    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/manifest+json' });
    manifestBlobUrl = URL.createObjectURL(blob);

    let link = document.querySelector('link[rel="manifest"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      document.head.appendChild(link);
    }
    link.href = manifestBlobUrl;
  } catch (error) {
    console.warn('[PWA] Failed to apply dynamic manifest:', error);
  }
}

/**
 * Update Apple touch icon for iOS
 */
export function updateAppleTouchIcon(logoUrl) {
  const href = resolveLogoUrl(logoUrl);
  if (!href) return;

  try {
    const existingIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (existingIcon) {
      existingIcon.remove();
    }

    const link = document.createElement('link');
    link.rel = 'apple-touch-icon';
    link.href = href;
    document.head.appendChild(link);
  } catch (error) {
    console.error('Error updating Apple touch icon:', error);
  }
}

/**
 * Update browser tab favicon from logo URL
 */
export function updateFavicon(logoUrl) {
  const base = resolveLogoUrl(logoUrl);
  if (!base) return;

  try {
    const faviconRels = ['icon', 'shortcut icon'];

    faviconRels.forEach((relValue) => {
      let link = document.querySelector(`link[rel="${relValue}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = relValue;
        document.head.appendChild(link);
      }

      link.type = 'image/png';
      link.href = base;
    });
  } catch (error) {
    console.error('Error updating favicon:', error);
  }
}

/**
 * Update PWA-related document metadata (title, favicon, apple-touch-icon, manifest name).
 */
export async function updatePWAManifest(settings) {
  if (!settings) return;

  const merged = mergeInstallSettings(settings);
  const { app_logo, app_name } = merged;

  if (app_name) {
    document.title = app_name;
  }

  updateAppleTouchIcon(app_logo);
  updateFavicon(app_logo);

  if (app_name) {
    const appleTitleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (appleTitleMeta) {
      appleTitleMeta.content = shortAppName(app_name);
    }
  }

  await applyDynamicManifest({ app_name, app_logo });
}
